import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  Chip,
  fade,
  darken,
  useMediaQuery,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import { useForm } from "react-hook-form";
import _ from "lodash";
import { useDispatch } from "react-redux";

import CategoryValidIn from "../../../MenusNewTest/CategoryValidIn";
import CategoryEditModal from "../../../MenusNewTest/Modal/CategoryEditModal";
import ReorderModal from "./ReorderModal";
import WrapperNode from "./WrapperNode";
import { CustomTheme } from "../../../../../../types/customTheme";
import { updateCategoryInStore } from "../../../../../../redux/actions/menu";

interface CategorySectionProps {
  category: any;
  isGridView: boolean;
  sortOption: string;
  selectedAvailability: string[];
  theme: CustomTheme;
  isMobile: boolean;
  locationId: string;
  menuNodeList: any;
  selectedMenuId: string;
  setMenuNodeList: any;
  productVersion: any;
  tags: any;
  setSuccess?: (message: string) => void;
  setError?: (message: string) => void;
  // Directory Edit Modal Props
  handleOpenDirectoryEditModal?: (
    product: any,
    wrapper: any,
    category: any,
    isOpenWrapperEditModal: any,
    isRootValue: any,
  ) => void;
  handleOpenProductEditModal?: (
    product: any,
    wrapper: any,
    category: any,
    isOpenWrapperEditModal: any,
    isRootValue: any,
  ) => void;
  handleOpenMappingModal?: (
    event: any,
    category: any,
    item: any,
    type: string,
    isMap?: any,
  ) => void;
  handleOpenProductOrDirectoryModals?: (category: any, wrapper?: any) => void;
  setIsProductAdd?: any;
  onDirectoryNavigate?: (directory: any, category: any, menuItem: any) => void;
  onProductNavigate?: (
    product: any,
    wrapper: any,
    category: any,
    menuItem: any,
  ) => void;
  currentCategory?: any;
  currentMenuItem?: any;
  isOpenWrapperEditModal?: any;
  isRoot?: any;
  firstWrapperId?: any;
  wrapperId?: any;
  removedLinkedIdLists?: any;
  handleClickLinkedId?: any;
  handleClearStrip?: any;
  handleNavigate?: any;
  // Props for directory view
  isInDirectory?: boolean;
  currentDirectoryInfo?: any;
  onReorderComplete?: () => void;
  isEditDisabled: boolean;
}

const CategorySection: React.FC<CategorySectionProps> = ({
  category,
  isGridView,
  sortOption,
  selectedAvailability,
  theme,
  isMobile,
  locationId,
  menuNodeList,
  selectedMenuId,
  setMenuNodeList,
  productVersion,
  tags,
  setSuccess,
  setError,
  handleOpenDirectoryEditModal,
  handleOpenProductEditModal,
  handleOpenMappingModal,
  handleOpenProductOrDirectoryModals,
  setIsProductAdd,
  onDirectoryNavigate,
  onProductNavigate,
  currentCategory,
  currentMenuItem,
  isOpenWrapperEditModal,
  isRoot,
  firstWrapperId,
  wrapperId,
  removedLinkedIdLists,
  handleClickLinkedId,
  handleClearStrip,
  handleNavigate,
  isInDirectory,
  currentDirectoryInfo,
  onReorderComplete,
  isEditDisabled,
}) => {
  const dispatch = useDispatch();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [forceUpdateKey, setForceUpdateKey] = useState(0);
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [currentReorderItems, setCurrentReorderItems] = useState<any[]>([]);
  const [isOpenCategoryEditModal, setIsOpenCategoryEditModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>({});
  const [isValidError, setIsValidError] = useState<any>({});
  const [isValidCategory, setIsValidCategory] = useState(true);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [buttonSize, setButtonSize] = useState<any>({ id: 1, name: "Small" });
  const [color, setColor] = useState("");
  const [kiosk, setKiosk] = useState("");
  const [isValidKiosk, setIsValidKiosk] = useState(true);
  const [openKioskDropDown, setOpenKioskDropDown] = useState(false);

  // Initialize react-hook-form
  const {
    register,
    handleSubmit: handleFormSubmit,
    errors,
    setValue,
    reset,
    formState: { isDirty },
  } = useForm<any>({
    defaultValues: {
      name: "",
      desc: "",
    },
  });

  // Set form values when selectedCategory changes
  useEffect(() => {
    if (selectedCategory && Object.keys(selectedCategory).length > 0) {
      setValue("name", selectedCategory.name || "");
      setValue("desc", selectedCategory.desc || "");
      setIsValidCategory(
        selectedCategory.valid !== undefined ? selectedCategory.valid : true,
      );
      setButtonSize({
        id: selectedCategory.buttonSize || 1,
        name: selectedCategory.buttonSize?.toString() || "Small",
      });
      setColor(selectedCategory.colour || "");
      setIsValidKiosk(selectedCategory.kiosk?.toString() === "1");
    }
  }, [selectedCategory, setValue]);

  const wrapperObjects = category.wrapper
    ? Object.values(category.wrapper)
    : [];

  let filteredItems = wrapperObjects;
  if (selectedAvailability.length === 1) {
    const showAvailable = selectedAvailability.includes("available");
    filteredItems = wrapperObjects.filter((item: any) =>
      showAvailable ? item?.valid : !item?.valid,
    );
  }

  // ✅ FIXED: Enhanced sort with secondary criteria for stability
  const sortedItems = [...filteredItems].sort((a: any, b: any) => {
    // Primary sort
    if (sortOption === "name-asc") {
      const nameCompare = (a.name || "").localeCompare(b.name || "");
      if (nameCompare !== 0) return nameCompare;
      // ✅ Secondary sort - viewOrder
      const viewOrderCompare = (a.viewOrder || 0) - (b.viewOrder || 0);
      if (viewOrderCompare !== 0) return viewOrderCompare;
      // ✅ Tertiary sort - id (stable)
      return (a.id || 0) - (b.id || 0);
    }

    if (sortOption === "name-desc") {
      const nameCompare = (b.name || "").localeCompare(a.name || "");
      if (nameCompare !== 0) return nameCompare;
      // ✅ Secondary sort - viewOrder
      const viewOrderCompare = (a.viewOrder || 0) - (b.viewOrder || 0);
      if (viewOrderCompare !== 0) return viewOrderCompare;
      // ✅ Tertiary sort - id (stable)
      return (a.id || 0) - (b.id || 0);
    }

    // Default - viewOrder
    const viewOrderCompare = (a.viewOrder || 0) - (b.viewOrder || 0);
    if (viewOrderCompare !== 0) return viewOrderCompare;

    // ✅ Tertiary sort - id (stable)
    return (a.id || 0) - (b.id || 0);
  });

  const handleAvailableCount = (wrapper: any) => {
    if (!wrapper) return 0;
    return Object.values(wrapper).filter((item: any) => item?.valid).length;
  };

  const handleUnavailableCount = (wrapper: any) => {
    if (!wrapper) return 0;
    return Object.values(wrapper).filter((item: any) => !item?.valid).length;
  };

  const handleOpenCategoryEditModal = (cat: any) => {
    setSelectedCategory(cat);
    setIsOpenCategoryEditModal(true);
  };

  const handleCloseCategoryEditModal = () => {
    setIsOpenCategoryEditModal(false);
    setSelectedCategory({});
    reset({ name: "", desc: "" });
    setIsValidCategory(true);
    setButtonSize({ id: 1, name: "Small" });
    setColor("");
    setIsValidKiosk(true);
    // Refresh the menu list after category edit
    setForceUpdateKey((prev) => prev + 1);
  };

  const handleOpenReorderModal = (categoryData: any) => {
    const items = categoryData?.wrapper
      ? Object.values(categoryData.wrapper)
      : [];
    const sortedItems = [...items].sort(
      (a: any, b: any) => (a.viewOrder || 0) - (b.viewOrder || 0),
    );
    setCurrentReorderItems(sortedItems);
    setIsReorderModalOpen(true);
  };

  const handleCloseReorderModal = (
    isRequest: boolean,
    finalOrderedItems?: any[],
  ) => {
    setIsReorderModalOpen(false);
    if (isRequest && finalOrderedItems) {
      const updatedWrapper: any = {};
      finalOrderedItems.forEach((item: any, idx: number) => {
        updatedWrapper[item.id] = {
          ...item,
          viewOrder: idx,
        };
      });

      const updatedMenuList = _.cloneDeep(menuNodeList);
      if (updatedMenuList[selectedMenuId]?.category[category.id]) {
        const existingWrapper =
          updatedMenuList[selectedMenuId].category[category.id].wrapper || {};
        updatedMenuList[selectedMenuId].category[category.id].wrapper = {
          ...existingWrapper,
          ...updatedWrapper,
        };
        setMenuNodeList(updatedMenuList);
        setForceUpdateKey((prev) => prev + 1);

        if (setSuccess) {
          setSuccess("Order updated successfully");
        }
      }
    }
  };

  const handleItemSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleOpenProductOrDirectoryModalLocal = (cat: any) => {
    if (handleOpenProductOrDirectoryModals) {
      handleOpenProductOrDirectoryModals(cat, null);
    }
  };

  const handleSelectAll = (isChecked: boolean, currentWrapper: any) => {
    let idsInWrapper: string[] = [];
    if (currentWrapper) {
      Object.values(currentWrapper).forEach((item: any) => {
        if (item?.id && !item?.directory) {
          idsInWrapper.push(item.id.toString());
        }
      });
    }
    if (isChecked) {
      setSelectedIds((prev) => [...new Set([...prev, ...idsInWrapper])]);
    } else {
      setSelectedIds((prev) => prev.filter((id) => !idsInWrapper.includes(id)));
    }
  };

  // Helper function to update category in Redux after edit or valid change
  const handleCategoryUpdated = (updatedCategory: any) => {
    const updatedCategoryForRedux = {
      id: updatedCategory.id,
      name: updatedCategory.name,
      desc: updatedCategory.desc,
      valid: updatedCategory.valid,
      colour: updatedCategory.colour,
      img: updatedCategory.img,
      kiosk: updatedCategory.kiosk,
      buttonSize: updatedCategory.buttonSize,
      viewOrder: updatedCategory.viewOrder,
      version: updatedCategory.version,
      menuId: updatedCategory.menuId,
    };

    dispatch(updateCategoryInStore(updatedCategoryForRedux));
    setForceUpdateKey((prev) => prev + 1);
  };

  return (
    <>
      <div key={category.id}>
        <Box
          style={{
            backgroundColor: theme.palette.background.default,
            padding: "20px 24px 10px 24px",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            style={{ gap: "12px" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "12px",
              }}
            >
              {category?.img ? (
                <img
                  src={category?.img}
                  alt=""
                  style={{
                    objectFit: "cover",
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                  }}
                />
              ) : (
                <Box
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: theme.palette.background.imgBackground,
                  }}
                >
                  <ImageOutlinedIcon style={{ fontSize: "40px" }} />
                </Box>
              )}
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    align="left"
                    style={{
                      fontSize: isMobile ? "16px" : "20px",
                      fontWeight: "bold",
                    }}
                  >
                    {category.name}
                  </Typography>
                  {!category.valid && (
                    <Box
                      style={{
                        backgroundColor: theme.palette.custom.red.main,
                        color: theme.palette.custom.red.contrastText,
                        fontSize: "10px",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      <Typography
                        variant="caption"
                        style={{ fontWeight: "bold", color: "white" }}
                      >
                        Deactivated
                      </Typography>
                    </Box>
                  )}
                  <Box
                    style={{
                      backgroundColor:
                        theme.palette.background.entity_highlight_background,
                      borderRadius: "10px",
                      padding: "4px 8px",
                    }}
                  >
                    <Typography
                      align="left"
                      style={{
                        fontSize: "10px",
                        color: theme.palette.secondary.main,
                      }}
                    >
                      {Object.values(category.wrapper)?.length || 0} Items
                    </Typography>
                  </Box>
                </div>
                <Typography
                  align="left"
                  style={{
                    fontSize: isMobile ? "11px" : "12px",
                    color: theme.palette.text.secondary,
                  }}
                >
                  {category.desc}
                </Typography>
              </div>
            </div>
            {productVersion !== 1 && (
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  size="small"
                  onClick={() => handleOpenCategoryEditModal(category)}
                  style={{
                    minWidth: "28px",
                    height: "32px",
                    backgroundColor: theme.palette.custom.yellow.main,
                  }}
                >
                  <EditIcon style={{ fontSize: "16px" }} />
                </Button>
                <CategoryValidIn
                  category={category}
                  setError={setError || console.log}
                  setIsValidError={setIsValidError}
                  setSuccess={setSuccess || console.log}
                  menuNodeList={menuNodeList}
                  setMenuNodeList={setMenuNodeList}
                  menuId={selectedMenuId}
                  onCategoryUpdated={handleCategoryUpdated}
                />
              </div>
            )}
          </Box>

          <Grid
            container
            spacing={2}
            style={{ marginBottom: "24px", marginTop: "20px" }}
          >
            <Grid item xs={6} sm={6} md={3}>
              <Box
                style={{
                  backgroundColor: theme.palette.background.entity_background,
                  borderRadius: "10px",
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  border: `1px solid ${theme.palette.background.entity_border}`,
                }}
              >
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50px",
                    border: "none",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: "16px",
                    backgroundColor: darken(theme.palette.secondary.main, 0.6),
                  }}
                >
                  <Icon
                    icon="fluent:layer-24-filled"
                    width={isMobile ? 20 : 28}
                    color={"white"}
                  />
                </div>
                <div>
                  <Typography
                    style={{
                      fontSize: isMobile ? "16px" : "20px",
                      fontWeight: "bold",
                    }}
                    align="left"
                  >
                    {Object.values(category?.wrapper)?.length || 0}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: isMobile ? "10px" : "12px",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Total Items
                  </Typography>
                </div>
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Box
                style={{
                  backgroundColor: theme.palette.background.entity_background,
                  borderRadius: "10px",
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  border: `1px solid ${theme.palette.background.entity_border}`,
                }}
              >
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50px",
                    border: "none",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: "16px",
                    backgroundColor: darken(
                      theme.palette.custom.green.main || "",
                      0.4,
                    ),
                  }}
                >
                  <Icon
                    icon="simple-line-icons:check"
                    width={isMobile ? 20 : 28}
                    color={theme.palette.custom.green.main}
                  />
                </div>
                <div>
                  <Typography
                    style={{
                      fontSize: isMobile ? "16px" : "20px",
                      fontWeight: "bold",
                    }}
                    align="left"
                  >
                    {handleAvailableCount(category?.wrapper) || 0}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: isMobile ? "10px" : "12px",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Available
                  </Typography>
                </div>
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Box
                style={{
                  backgroundColor: theme.palette.background.entity_background,
                  borderRadius: "10px",
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  border: `1px solid ${theme.palette.background.entity_border}`,
                }}
              >
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50px",
                    border: "none",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: "16px",
                    backgroundColor: darken(
                      theme.palette.custom.red.main || "",
                      0.4,
                    ),
                  }}
                >
                  <Icon
                    icon="icon-park-outline:close-one"
                    width={isMobile ? 20 : 30}
                    color={theme.palette.custom.red.main}
                  />
                </div>
                <div>
                  <Typography
                    style={{
                      fontSize: isMobile ? "16px" : "20px",
                      fontWeight: "bold",
                    }}
                    align="left"
                  >
                    {handleUnavailableCount(category?.wrapper) || 0}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: isMobile ? "10px" : "12px",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Unavailable
                  </Typography>
                </div>
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Box
                onClick={() => handleOpenReorderModal(category)}
                style={{
                  backgroundColor: theme.palette.background.entity_background,
                  borderRadius: "10px",
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  border: `1px solid ${theme.palette.background.entity_border}`,
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50px",
                    border: "none",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: "16px",
                    backgroundColor: darken(theme.palette.secondary.main, 0.6),
                  }}
                >
                  <Icon
                    icon="fluent:arrow-sort-24-filled"
                    width={isMobile ? 20 : 28}
                    color="white"
                  />
                </div>
                <div>
                  <Typography
                    style={{
                      fontSize: isMobile ? "16px" : "20px",
                      fontWeight: "bold",
                    }}
                    align="left"
                  >
                    Reorder
                  </Typography>
                  <Typography
                    style={{
                      fontSize: isMobile ? "10px" : "12px",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Custom order
                  </Typography>
                </div>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Grid
          container
          spacing={isGridView ? 2 : 0}
          style={{ padding: "0 24px 24px 24px" }}
        >
          {sortedItems.map((wrapperItem: any) => (
            <WrapperNode
              // ✅ FIXED: Removed idx from key - using only stable id
              key={`wrapper-${wrapperItem?.id}`}
              wrapper={wrapperItem}
              category={category}
              isGridView={isGridView}
              selectedIds={selectedIds}
              handleItemSelect={handleItemSelect}
              tags={tags}
              menuNodeList={menuNodeList}
              setMenuNodeList={setMenuNodeList}
              menuId={selectedMenuId}
              categoryId={category.id}
              productVersion={productVersion}
              locationId={locationId}
              onDirectoryVisibilityUpdated={() =>
                setForceUpdateKey((prev) => prev + 1)
              }
              forceUpdateKey={forceUpdateKey}
              handleSelectAll={handleSelectAll}
              // Pass directory edit props
              handleOpenDirectoryEditModal={handleOpenDirectoryEditModal}
              handleOpenProductEditModal={handleOpenProductEditModal}
              handleOpenMappingModal={handleOpenMappingModal}
              handleOpenProductOrDirectoryModals={
                handleOpenProductOrDirectoryModals
              }
              setIsProductAdd={setIsProductAdd}
              setError={setError}
              onDirectoryNavigate={onDirectoryNavigate}
              onProductNavigate={onProductNavigate}
              currentCategory={currentCategory}
              currentMenuItem={currentMenuItem}
              isOpenWrapperEditModal={isOpenWrapperEditModal}
              isRoot={isRoot}
              firstWrapperId={firstWrapperId}
              wrapperId={wrapperId}
              removedLinkedIdLists={removedLinkedIdLists}
              handleClickLinkedId={handleClickLinkedId}
              handleClearStrip={handleClearStrip}
              handleNavigate={handleNavigate}
              isEditDisabled={isEditDisabled}
            />
          ))}
          {productVersion !== 1 && (
            <Grid item md={6} xs={12} lg={4}>
              <Button
                variant="contained"
                style={{
                  width: "100%",
                  marginTop: "14px",
                  display: "flex",
                  borderRadius: "10px",
                  justifyContent: "center",
                  alignItems: "center",
                  textTransform: "none",
                  backgroundColor: theme.palette.background.entity_background,
                  border: `1px dashed ${theme.palette.custom.yellow.main}`,
                  height: isMobile ? "100px" : "auto",
                }}
                onClick={() => handleOpenProductOrDirectoryModalLocal(category)}
              >
                <Typography>
                  <AddIcon fontSize="large" />
                </Typography>
                <Typography style={{ marginLeft: "12px" }}>Add New</Typography>
              </Button>
            </Grid>
          )}
        </Grid>
      </div>

      {/* Category Edit Modal */}
      <CategoryEditModal
        isOpen={isOpenCategoryEditModal}
        setIsOpen={setIsOpenCategoryEditModal}
        register={register}
        errors={errors}
        setValue={setValue}
        reset={reset}
        handleCloseCategoryEditModal={handleCloseCategoryEditModal}
        isLoadingButton={isLoadingButton}
        selectedCategory={selectedCategory}
        setIsValidCategory={setIsValidCategory}
        isValidCategory={isValidCategory}
        buttonSize={buttonSize}
        setButtonSize={setButtonSize}
        color={color}
        setColor={setColor}
        kiosk={kiosk}
        setKiosk={setKiosk}
        setOpenKioskDropDown={setOpenKioskDropDown}
        openKioskDropDown={openKioskDropDown}
        isValidKiosk={isValidKiosk}
        setIsValidKiosk={setIsValidKiosk}
        isDirty={isDirty}
        setError={setError || console.log}
        setIsLoadingButton={setIsLoadingButton}
        handleSubmit={handleFormSubmit}
        selectedMenuId={selectedMenuId}
        setSuccess={setSuccess || console.log}
        menuNodeList={menuNodeList}
        setMenuNodeList={setMenuNodeList}
        setIsCategoryNameEmpty={() => {}}
        onCategoryUpdated={handleCategoryUpdated}
      />

      {/* Reorder Modal */}
      <ReorderModal
        open={isReorderModalOpen}
        onClose={handleCloseReorderModal}
        items={currentReorderItems}
        category={category}
        menuId={selectedMenuId}
        categoryId={category.id}
        onSave={setCurrentReorderItems}
        setSuccess={setSuccess || console.log}
        setError={setError || console.log}
        getProductWrapperInfo={() => {}}
        menuNode={menuNodeList}
        categoryNode={[]}
        productVersion={productVersion}
        onReorderComplete={onReorderComplete}
      />
    </>
  );
};

export default CategorySection;
